/** The activity record's card layout (plan §3 "Cards"): the registry and one activity → the
 *  eight cards in page order (About · Place · Visit · Contact & links · Nearby · Access ·
 *  Identity & source · Technical), each split into its Editable block, the muted read-only
 *  block of values no human edits — "From Google" (the linked place and its stored data) or
 *  "From rating sources" (the ratings: a total over every source, with or without a place) —
 *  and the System block. Pure — the cards' FieldSlots read the draft themselves.
 *
 *  The legacy texts no UI edits (`notes`, `ontrip_description`, `description_abbrev`) are
 *  laid out only when they hold something (plan §3). */
import type { ActivityDetail } from '@/api/activities';
import {
  ACTIVITY_RECORD_SECTIONS,
  type ActivityRecordFieldKey,
  type ActivityRecordSection,
} from '@/api/activityRecord';
import type { FieldBlockKind, ViewBlock, ViewSection } from '@/features/records/views/types';

import {
  ACTIVITY_ENTITY,
  ACTIVITY_RECORD_REGISTRY,
  ACTIVITY_SECTION_TITLES,
  activityPlacements,
  type ActivityRegistry,
} from '../registry';

/** Where a card's read-only (vendor) block comes from: its muted heading and note. */
export interface ActivityVendorSource {
  label: string;
  note: string;
}

/** The linked Google place: its id and the data stored from it. */
export const FROM_GOOGLE: ActivityVendorSource = { label: 'From Google', note: 'read-only, from the linked place' };

/** The rating sources (the detail's `rating_count`): the stored rating is the total over all
 *  of them, whether or not the activity has a Google place. */
export const FROM_RATING_SOURCES: ActivityVendorSource = {
  label: 'From rating sources',
  note: 'read-only, the total over all its sources',
};

const VENDOR_SOURCE: Partial<Record<ActivityRecordFieldKey, ActivityVendorSource>> = {
  ratings: FROM_RATING_SOURCES,
};

/** A read-only value's source; the Google place unless named otherwise. */
export function activityVendorSource(key: ActivityRecordFieldKey): ActivityVendorSource {
  return VENDOR_SOURCE[key] ?? FROM_GOOGLE;
}

/** The DOM id of a card — what the number keys and the band's cells scroll to. */
export function activitySectionAnchorId(section: ActivityRecordSection): string {
  return `activity-section-${section}`;
}

const LEGACY: ReadonlySet<ActivityRecordFieldKey> = new Set(['notes', 'ontrip_description', 'description_abbrev']);

const BLOCK_ORDER: FieldBlockKind[] = ['editable', 'vendor', 'system'];

const blank = (v: string | null | undefined) => !v || v.trim() === '';

/** What the layout reads of an activity: its id, and the legacy texts it lays out only when
 *  they hold something. */
export type ActivityLayoutFacts = Pick<ActivityDetail, 'id' | 'notes' | 'ontrip_description' | 'description_abbrev'>;

/** Whether a key is laid out for this activity. */
function shown(key: ActivityRecordFieldKey, a: ActivityLayoutFacts): boolean {
  if (!LEGACY.has(key)) return true;
  return !blank(a[key as 'notes' | 'ontrip_description' | 'description_abbrev']);
}

export function activitySections(
  a: ActivityLayoutFacts,
  registry: ActivityRegistry = ACTIVITY_RECORD_REGISTRY,
): ViewSection<ActivityRecordSection>[] {
  const placements = activityPlacements(registry);
  return ACTIVITY_RECORD_SECTIONS.map((section) => {
    const keys = (placements.get(section) ?? []).filter((k) => shown(k, a));
    const blocks: ViewBlock[] = [];
    for (const kind of BLOCK_ORDER) {
      const fields = keys
        .filter((k) => registry[k].block === kind)
        .map((k) => ({ key: k, entity: ACTIVITY_ENTITY, id: a.id, path: k }));
      if (fields.length === 0) continue;
      // A card's read-only block has one source (the layout test pins it): its first field's.
      const source = kind === 'vendor' ? activityVendorSource(fields[0]!.key as ActivityRecordFieldKey) : null;
      blocks.push({
        kind,
        ...(source ? { label: source.label, note: source.note } : kind === 'system' ? { label: 'System' } : {}),
        fields,
      });
    }
    return { id: section, title: ACTIVITY_SECTION_TITLES[section], blocks };
  });
}
